import { cn } from "@postdaily/ui/lib/utils";
import { Link } from "@tanstack/react-router";
import {
  ArrowRight,
  Briefcase,
  ChevronDown,
  CircleHelp,
  FileText,
  Handshake,
  Info,
  type LucideIcon,
  Menu,
  Sparkles,
  X,
} from "lucide-react";
import { useState } from "react";

import { MARKETING_FLAGS } from "@/lib/marketing-flags";
import { PLATFORM_CATALOG } from "@/lib/platforms";
import { lookupBy } from "@/lib/table-lookup";
import { BrandMark } from "./brand-mark";
import { MarketingLogo } from "./logo";
import {
  MarketingCompactAuthActions,
  MarketingDesktopAuthActions,
  MarketingMobileAuthActions,
} from "./marketing-auth-actions";

/**
 * Marketing nav points at destinations that exist today: conversion CTAs go to
 * `/login`; nav + resource links that aren't built yet fall back to the landing
 * route (`/`) so nothing 404s. Swap the placeholders as those routes land.
 */
const HOME = "/" as const;

/** Hide nav links for pages that are gated off pre-launch (see marketing-flags). */
const isVisibleLabel = (label: string) => {
  if (label === "Blog") return MARKETING_FLAGS.blog;
  if (label === "Careers") return MARKETING_FLAGS.careers;
  if (label === "Changelog") return MARKETING_FLAGS.changelog;
  if (label === "Creator Partner Program") return MARKETING_FLAGS.partners;
  return true;
};

export type MarketingNav =
  | "home"
  | "features"
  | "integrations"
  | "pricing"
  | "blog";

const navLinks = [
  { label: "Features", key: "features", to: "/features" },
  { label: "Integrations", key: "integrations", to: "/integrations" },
  { label: "Pricing", key: "pricing", to: "/pricing" },
] as const;

const resources: {
  label: string;
  description: string;
  icon: LucideIcon;
  to:
    | "/"
    | "/about"
    | "/changelog"
    | "/help"
    | "/careers"
    | "/blog"
    | "/partners";
}[] = [
  {
    label: "Blog",
    description: "Playbooks, tips & product news",
    icon: FileText,
    to: "/blog",
  },
  {
    label: "Changelog",
    description: "What's new in PostDaily",
    icon: Sparkles,
    to: "/changelog",
  },
  {
    label: "Help center",
    description: "Guides, answers & support",
    icon: CircleHelp,
    to: "/help",
  },
  {
    label: "About",
    description: "Our story & the team",
    icon: Info,
    to: "/about",
  },
  {
    label: "Careers",
    description: "We're hiring across the team",
    icon: Briefcase,
    to: "/careers",
  },
  {
    label: "Creator Partner Program",
    description: "Earn recurring commission on referrals",
    icon: Handshake,
    to: "/partners",
  },
];

/**
 * The resource links whose pages are actually live.
 *
 * `isVisibleLabel` reads only module-level flags, so this subset is fixed at
 * module scope — it does not need recomputing on every render.
 */
const visibleResources = resources.filter((item) => isVisibleLabel(item.label));

const mobileLinks = [
  "Features",
  "Integrations",
  "Pricing",
  "Blog",
  "Changelog",
  "Help center",
  "About",
  "Contact",
  "Creator Partner Program",
] as const;

/** Mobile-menu link targets for pages that are built; others fall back to home. */
const MOBILE_ROUTES = {
  Features: "/features",
  Integrations: "/integrations",
  Pricing: "/pricing",
  Blog: "/blog",
  Changelog: "/changelog",
  "Help center": "/help",
  About: "/about",
  Contact: "/contact",
  "Creator Partner Program": "/partners",
} as const;

const navLinkClass =
  "relative inline-flex h-[38px] items-center rounded-[9px] px-[13px] text-[14.5px] font-medium text-[oklch(0.42_0.02_264)] no-underline transition-colors hover:text-foreground";

const megaMenuShadow =
  "shadow-[0_1px_2px_oklch(0.4_0.05_264/0.05),0_22px_50px_-18px_oklch(0.4_0.08_264/0.28)]";

function NavActiveUnderline() {
  return (
    <span
      className="absolute inset-x-[13px] bottom-[-19px] h-0.5 rounded-sm"
      style={{
        backgroundImage:
          "linear-gradient(90deg,oklch(0.58 0.17 264),oklch(0.62 0.17 289))",
      }}
    />
  );
}

export function SiteHeader({ active = "home" }: { active?: MarketingNav }) {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="sticky top-0 z-[60] border-border/70 border-b bg-background/70 backdrop-blur-md backdrop-saturate-150">
      <div className="mx-auto flex h-[68px] max-w-[1200px] items-center justify-between gap-4 px-6">
        <MarketingLogo />

        <nav className="hidden items-center gap-px min-[900px]:flex">
          {navLinks.map((link) => {
            if (link.key === "integrations") {
              return (
                <div key={link.key} className="group relative">
                  <Link
                    to={link.to}
                    className={cn(
                      navLinkClass,
                      "gap-1.5 group-hover:bg-muted group-hover:text-foreground",
                    )}
                  >
                    {link.label}
                    <ChevronDown
                      className="size-[15px] opacity-60 transition-transform group-hover:rotate-180"
                      strokeWidth={2.2}
                    />
                    {active === link.key && <NavActiveUnderline />}
                  </Link>
                  {/* Padding bridge keeps hover alive across the gap under the trigger */}
                  <div className="invisible absolute top-full left-1/2 z-50 w-[min(560px,calc(100vw-2rem))] -translate-x-1/2 pt-2.5 opacity-0 transition-opacity group-focus-within:visible group-focus-within:opacity-100 group-hover:visible group-hover:opacity-100">
                    <div
                      className={cn(
                        "rounded-2xl border border-border bg-popover p-5",
                        megaMenuShadow,
                      )}
                    >
                      <p className="mb-3.5 px-1 font-semibold text-[11px] text-muted-foreground tracking-[0.08em]">
                        CHANNELS
                      </p>
                      <div className="grid grid-cols-3 gap-x-2 gap-y-0.5">
                        {PLATFORM_CATALOG.map((channel) => (
                          <Link
                            key={channel.id}
                            to="/integrations"
                            className="flex items-center gap-2.5 rounded-[10px] px-2.5 py-2 text-foreground no-underline transition-colors hover:bg-muted"
                          >
                            <BrandMark
                              platform={channel.id}
                              className="size-[22px] rounded-[6px]"
                              iconClassName="size-[12px]"
                            />
                            <span className="truncate font-medium text-[13.5px]">
                              {channel.label}
                            </span>
                          </Link>
                        ))}
                      </div>
                      <div className="mt-3 border-border border-t pt-3">
                        <Link
                          to="/integrations"
                          className="inline-flex items-center gap-1.5 px-2.5 text-[13px] text-muted-foreground no-underline transition-colors hover:text-foreground"
                        >
                          See all integrations
                          <ArrowRight className="size-3.5" strokeWidth={2.2} />
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <Link key={link.key} to={link.to} className={navLinkClass}>
                {link.label}
                {active === link.key && <NavActiveUnderline />}
              </Link>
            );
          })}

          <div className="group relative">
            <button
              type="button"
              className={cn(
                navLinkClass,
                "cursor-pointer gap-1.5 border-0 bg-transparent group-hover:bg-muted group-hover:text-foreground",
              )}
            >
              Resources
              <ChevronDown
                className="size-[15px] opacity-60 transition-transform group-hover:rotate-180"
                strokeWidth={2.2}
              />
            </button>
            <div className="invisible absolute top-full left-1/2 z-50 w-[340px] -translate-x-1/2 pt-2.5 opacity-0 transition-opacity group-focus-within:visible group-focus-within:opacity-100 group-hover:visible group-hover:opacity-100">
              <div
                className={cn(
                  "rounded-2xl border border-border bg-popover p-2",
                  megaMenuShadow,
                )}
              >
                {visibleResources.map((item) => (
                  <Link
                    key={item.label}
                    to={item.to}
                    className="flex items-center gap-3 rounded-[11px] p-[9px_11px] no-underline transition-colors hover:bg-muted"
                  >
                    <span className="inline-flex size-[34px] shrink-0 items-center justify-center rounded-[9px] bg-[oklch(0.95_0.03_264)] text-primary">
                      <item.icon className="size-[17px]" strokeWidth={2} />
                    </span>
                    <span className="flex flex-col gap-px">
                      <span className="font-semibold text-foreground text-sm">
                        {item.label}
                      </span>
                      <span className="text-[12.5px] text-muted-foreground">
                        {item.description}
                      </span>
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </nav>

        <div className="flex items-center gap-[9px]">
          <MarketingDesktopAuthActions />
          <MarketingCompactAuthActions />
          <button
            type="button"
            aria-label="Menu"
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((open) => !open)}
            className="inline-flex size-[42px] items-center justify-center rounded-[11px] border border-border bg-card text-foreground min-[900px]:hidden"
          >
            {mobileOpen ? (
              <X className="size-5" />
            ) : (
              <Menu className="size-5" />
            )}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="border-border border-t bg-background px-[18px] pt-3 pb-5 min-[900px]:hidden">
          {mobileLinks.flatMap((label) =>
            isVisibleLabel(label)
              ? [
                  <Link
                    key={label}
                    to={lookupBy(MOBILE_ROUTES, label) ?? HOME}
                    onClick={() => setMobileOpen(false)}
                    className="block rounded-[11px] p-3 font-semibold text-base text-foreground no-underline hover:bg-muted"
                  >
                    {label}
                  </Link>,
                ]
              : [],
          )}
          <MarketingMobileAuthActions onNavigate={() => setMobileOpen(false)} />
        </div>
      )}
    </header>
  );
}
